/**
 * Workflow Triggers Service
 * Automatically executes workflows on deal status changes
 * Framework B.1, C.3, C.4 compliant
 * 
 * Non-blocking: Trigger failures do not block deal transitions
 * Audit: All trigger attempts logged to workflow_logs table
 */

import { supabase } from '../lib/supabase';
import { sellerIssuesOwnInvoice } from '../lib/sellerType';
import { workflowLogFields, workflowLogStatus, type WorkflowLogStatus } from '../lib/workflowLogStatus';
import { smsService } from './smsService';
import { emailService } from './emailService';
import { ppsrService } from './ppsrService';
import { invoiceService } from './invoiceService';
import { xeroService } from './xeroService';
import { applyLotLoadFee } from './lotLoadFeeService';

export interface WorkflowResult {
  success: boolean;
  message: string;
  details?: Record<string, any>;
}

/**
 * Execute all triggers for a given status transition
 * Framework B.1: Triggers fire at specific statuses
 */
export const executeTriggersForStatus = async (
  dealId: string,
  toStatus: string,
  /*
   * Why the seller is being told a second time. The repeat guard refuses a
   * duplicate send without one, so a resend that carries no reason is simply
   * dropped - which is how a Vehicle Purchase Agreement came to be marked sent to two people
   * who never received it.
   */
  repeatReason?: string,
): Promise<WorkflowResult[]> => {
  const results: WorkflowResult[] = [];

  try {
    switch (toStatus) {
      // Under Offer → Send BOS SMS + email (email carries the BoS link since SMS is off in this MVP)
      case 'under_offer':
        results.push(await triggerBOS_SMS(dealId, repeatReason));
        results.push(await triggerBOS_Email(dealId, repeatReason));
        break;

      // Contract Signed → Send DD form + auto-book inspection
      case 'contract_signed':
        results.push(await triggerDD_Collection(dealId));
        results.push(await triggerInspectionBooking(dealId));
        break;

      // Paid → lot load fee, PPSR discharge, invoice, Xero sync
      case 'paid':
        results.push(await triggerLotLoadFee(dealId));
        results.push(await triggerPPSR_Discharge(dealId));
        results.push(await triggerInvoiceGeneration(dealId));
        results.push(await triggerXeroSync(dealId));
        break;

      default:
        break;
    }
  } catch (err) {
    console.error(`Error executing triggers for status ${toStatus}:`, err);
    /*
     * A thrown trigger used to vanish here. results stayed empty, and an empty
     * list is indistinguishable from "everything worked" - which is how a Bill
     * of Sale screen came to report a send that was never attempted. Record the
     * failure so every caller can see it.
     */
    results.push({
      success: false,
      message:
        'Some steps did not run: ' +
        (err instanceof Error ? err.message : 'Unknown error'),
    })
  }

  return results;
};

/**
 * TRIGGER 1: under_offer → Send BOS SMS to seller (14-day link)
 * Framework: Deal moves to under_offer automatically triggers SMS
 */
export const triggerBOS_SMS = async (dealId: string, repeatReason?: string): Promise<WorkflowResult> => {
  try {
    // Fetch deal + seller info
    const { data: deal, error } = await supabase
      .from('deals')
      .select('id, seller_name, seller_phone, deal_status, seller_token, seller_type')
      .eq('id', dealId)
      .single();

    if (error) throw error;
    /* Winnie, 11 Sep, section 3.

       A dealer, an auction house and a tender seller never sign our Vehicle Purchase
       Agreement, so texting one a link to sign it sends them to a page they have no
       business on. Their deal only passes through this stage on its way to Committed,
       carried by their own invoice; it does not stop here waiting for a signature.

       Refused here rather than at the caller, so it holds for every road into this
       trigger - the automatic one and the Send again button both. */
    if (sellerIssuesOwnInvoice(deal?.seller_type)) {
      const why =
        'Not sent: a dealer, auction or tender seller does not sign the Vehicle Purchase Agreement.'
      await logTrigger(dealId, 'BOS_SMS_SENT', 'skipped', why, {
        reason: 'seller_issues_own_invoice',
        seller_type: deal?.seller_type || null,
      });
      return { success: false, message: why };
    }

    if (!deal?.seller_phone) {
      await logTrigger(dealId, 'BOS_SMS_SENT', false, 'No seller phone number', {
        reason: 'seller_phone_missing'
      });
      return { success: false, message: 'No seller phone number' };
    }

    // Call SMS service (mocked)
    const result = await smsService.sendBOS_SMS(
      deal.seller_phone,
      deal.seller_name,
      dealId,
      deal.seller_token,
      repeatReason,
    );

    // Log to workflow_logs
    /*
     * A simulated send is not a send. With SMS_PROVIDER unset the endpoint
     * falls back to the mock provider and reports sent, so reading success
     * alone stamped bos_sms_sent_at and wrote a success row for a text nobody
     * received - and that column starts the seller signing-link clock.
     * arSmsCadence already draws this distinction; this is the same check.
     */
    const simulated = Boolean((result as unknown as { simulated?: boolean }).simulated)
    const reallySent = result.success && !simulated

    await logTrigger(dealId, 'BOS_SMS_SENT', reallySent, result.message, {
      phone: deal.seller_phone,
      message: result.details?.message,
      simulated,
    })

    if (reallySent) {
      await supabase
        .from('deals')
        .update({ bos_sms_sent_at: new Date().toISOString() })
        .eq('id', dealId)
    }

    if (simulated) {
      return {
        success: false,
        message: 'No SMS was sent: this environment has no SMS provider configured.',
        details: { ...(result.details || {}), simulated: true },
      }
    }

    return result;
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    await logTrigger(dealId, 'BOS_SMS_SENT', false, msg);
    return { success: false, message: msg };
  }
};

/**
 * TRIGGER 2: contract_signed → Send DD collection form link
 * Framework: Seller uploads documents, system tracks progress
 */
export const triggerDD_Collection = async (dealId: string): Promise<WorkflowResult> => {
  try {
    const { data: deal, error } = await supabase
      .from('deals')
      .select('id, seller_name, seller_email, seller_type, deal_status, seller_token')
      .eq('id', dealId)
      .single();

    if (error) throw error;
    if (!deal?.seller_email) {
      await logTrigger(dealId, 'DD_COLLECTION_INITIATED', false, 'No seller email', {
        reason: 'seller_email_missing'
      });
      return { success: false, message: 'No seller email' };
    }

    // Call email service (mocked)
    const result = await emailService.sendDD_CollectionForm(
      deal.seller_email,
      deal.seller_name,
      deal.seller_type,
      deal.seller_token,
      { dealId, trigger: 'DD_COLLECTION_INITIATED' }
    );

    // Log to workflow_logs
    await logTrigger(dealId, 'DD_COLLECTION_INITIATED', result.success, result.message, {
      email: deal.seller_email,
      seller_type: deal.seller_type
    });

    // Update deal tracking
    if (result.success) {
      await supabase
        .from('deals')
        .update({ dd_collection_email_sent_at: new Date().toISOString() })
        .eq('id', dealId);
    }

    return result;
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    await logTrigger(dealId, 'DD_COLLECTION_INITIATED', false, msg);
    return { success: false, message: msg };
  }
};

/**
 * TRIGGER 2b: under_offer → Email the seller the Vehicle Purchase Agreement link.
 * Framework Table 1 (Under Offer): "Vehicle Purchase Agreement link ... sent to seller."
 * SMS is disabled in this MVP, so email is the seller-facing channel here.
 */
export const triggerBOS_Email = async (dealId: string, repeatReason?: string): Promise<WorkflowResult> => {
  try {
    const { data: deal, error } = await supabase
      .from('deals')
      .select('id, seller_name, seller_email, seller_token, seller_type, vehicle_year, vehicle_make, vehicle_model, autograb_year, autograb_make, autograb_model')
      .eq('id', dealId)
      .single();

    if (error) throw error;
    /* The same rule the SMS above holds (Winnie, 11 Sep, section 3), which the email never
       did: a dealer, auction or tender seller does not sign our Vehicle Purchase Agreement,
       so emailing them a link to sign it sends them somewhere they have no business. Found
       5 Nov 2026 when a dealer deal reached Committed and the email went out while the SMS
       was correctly skipped. */
    if (sellerIssuesOwnInvoice(deal?.seller_type)) {
      const why =
        'Not sent: a dealer, auction or tender seller does not sign the Vehicle Purchase Agreement.'
      await logTrigger(dealId, 'BOS_EMAIL_SENT', 'skipped', why, {
        reason: 'seller_issues_own_invoice',
        seller_type: deal?.seller_type || null,
      });
      return { success: false, message: why };
    }
    if (!deal?.seller_email) {
      await logTrigger(dealId, 'BOS_EMAIL_SENT', false, 'No seller email', {
        reason: 'seller_email_missing'
      });
      return { success: false, message: 'No seller email' };
    }

    const year = deal.vehicle_year ?? deal.autograb_year;
    const make = deal.vehicle_make || deal.autograb_make || '';
    const model = deal.vehicle_model || deal.autograb_model || '';
    // Was `${appOrigin}/deals/${dealId}` — a real route, but only inside the
    // authenticated app. Sellers are not DirectAuto users, so the "View Deal &
    // Sign" button in this email dropped them on the login screen. It now
    // points at the seller's own page, which is the thing they can actually
    // sign on.
    const appOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://staging.directauto.net.au';
    if (!deal.seller_token) {
      await logTrigger(dealId, 'BOS_EMAIL_SENT', false, 'No seller link token', {
        reason: 'seller_token_missing',
      });
      return { success: false, message: 'This deal has no seller link token yet — run migration 025. Nothing was sent.' };
    }
    const dealLink = `${appOrigin}/seller/${deal.seller_token}`;

    const result = await emailService.sendDealNotification(
      deal.seller_email,
      deal.seller_name,
      { year: Number(year) || 0, make, model },
      dealLink,
      { dealId, trigger: 'BOS_EMAIL_SENT', repeatReason }
    );

    await logTrigger(dealId, 'BOS_EMAIL_SENT', result.success, result.message, {
      email: deal.seller_email
    });

    return result;
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    await logTrigger(dealId, 'BOS_EMAIL_SENT', false, msg);
    return { success: false, message: msg };
  }
};

/**
 * TRIGGER 3: contract_signed → Auto-book inspection or send manual invite
 * Framework: Inspection is part of committed workflow
 */
export const triggerInspectionBooking = async (dealId: string): Promise<WorkflowResult> => {
  try {
    const { data: deal, error } = await supabase
      .from('deals')
      .select('id, seller_location, inspection_status')
      .eq('id', dealId)
      .single();

    if (error) throw error;

    // Skip if already booked
    if (deal?.inspection_status === 'confirmed' || deal?.inspection_status === 'completed') {
      return {
        success: true,
        message: 'Inspection already booked'
      };
    }

    // Extract state from location
    const stateMatch = deal?.seller_location?.match(/\s([A-Z]{2,3})\s/);
    const state = stateMatch ? stateMatch[1] : null;

    if (!state) {
      await logTrigger(dealId, 'INSPECTION_BOOKING_INITIATED', false, 'Unable to extract state', {
        reason: 'state_extraction_failed'
      });
      return { success: false, message: 'Unable to extract state from seller location' };
    }

    // Try to find vendor for this state
    const { data: vendor } = await supabase
      .from('inspection_vendors')
      .select('*')
      .eq('primary_state', state)
      .eq('active', true)
      .limit(1)
      .single();

    if (!vendor) {
      // Manual booking required
      await logTrigger(dealId, 'INSPECTION_BOOKING_INITIATED', true, 'Manual booking required', {
        method: 'manual',
        state,
        reason: 'no_vendor_available'
      });

      return {
        success: true,
        message: 'Manual inspection booking required - no auto vendor available'
      };
    }

    // This used to write inspection_booking_method: 'auto' with a reference it
    // invented on the spot — `AB-<deal>-<timestamp>` — and report "Inspection
    // auto-booked with <vendor>". No request was ever made to the vendor.
    // inspection_vendors.booking_api_url and .api_key have existed since
    // migration 008 and were called from nowhere, so the entire booking lived
    // in our own database and nobody outside the building knew about it. A
    // booking nobody has agreed to is not a booking.
    //
    // Now: if the vendor has a booking endpoint we call it and only record a
    // booking when they confirm one. If they do not, we record a REQUEST and
    // say plainly that a person still has to place it.
    const reference = `DA-${dealId.substring(0, 8)}-${Date.now().toString(36)}`;

    if (!vendor.booking_api_url) {
      const { error: reqError } = await supabase
        .from('deals')
        .update({
          inspection_vendor_id: vendor.id,
          inspection_booking_method: 'manual_required',
          inspection_booking_reference: reference,
          inspection_status: 'awaiting_booking'
        })
        .eq('id', dealId);
      if (reqError) throw reqError;

      await logTrigger(
        dealId,
        'INSPECTION_BOOKING_INITIATED',
        true,
        `${vendor.vendor_name} has no booking API — a person needs to place this booking`,
        { method: 'manual_required', vendor_id: vendor.id, vendor_name: vendor.vendor_name, reference }
      );

      return {
        success: true,
        message: `Inspection not yet booked — call ${vendor.vendor_name} to place it`,
        details: { vendor: vendor.vendor_name, reference, requiresManualBooking: true }
      };
    }

    // The vendor does have an endpoint. Ask them, and believe only what they
    // send back.
    let confirmed: { reference?: string; scheduledFor?: string } | null = null;
    let vendorError: string | null = null;
    try {
      const res = await fetch(vendor.booking_api_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(vendor.api_key ? { Authorization: `Bearer ${vendor.api_key}` } : {})
        },
        body: JSON.stringify({ reference, state, deal_reference: dealId })
      });
      const body = await res.json().catch(() => null);
      // Not res.ok alone — the body has to carry a booking.
      if (res.ok && body && (body.booking_reference || body.reference)) {
        confirmed = {
          reference: body.booking_reference || body.reference,
          scheduledFor: body.scheduled_for || body.scheduledFor || undefined
        };
      } else {
        vendorError = body?.error || body?.message || `Vendor returned ${res.status}`;
      }
    } catch (e) {
      vendorError = e instanceof Error ? e.message : 'Could not reach the vendor';
    }

    if (!confirmed) {
      const { error: failErr } = await supabase
        .from('deals')
        .update({
          inspection_vendor_id: vendor.id,
          inspection_booking_method: 'manual_required',
          inspection_booking_reference: reference,
          inspection_status: 'awaiting_booking'
        })
        .eq('id', dealId);
      if (failErr) throw failErr;

      await logTrigger(
        dealId,
        'INSPECTION_BOOKING_INITIATED',
        false,
        `${vendor.vendor_name} did not confirm a booking: ${vendorError}`,
        { method: 'auto_failed', vendor_id: vendor.id, vendor_name: vendor.vendor_name, reference, vendorError }
      );

      return {
        success: false,
        message: `${vendor.vendor_name} did not confirm the booking — place it by phone. (${vendorError})`,
        details: { vendor: vendor.vendor_name, reference, requiresManualBooking: true }
      };
    }

    const { error: updateError } = await supabase
      .from('deals')
      .update({
        inspection_vendor_id: vendor.id,
        inspection_booking_method: 'auto',
        inspection_booking_reference: confirmed.reference,
        inspection_booked_at: new Date().toISOString(),
        inspection_scheduled_for: confirmed.scheduledFor || null,
        inspection_status: 'pending'
      })
      .eq('id', dealId);

    if (updateError) throw updateError;

    await logTrigger(dealId, 'INSPECTION_BOOKING_INITIATED', true, `Booked with ${vendor.vendor_name}`, {
      method: 'auto',
      vendor_id: vendor.id,
      vendor_name: vendor.vendor_name,
      booking_ref: confirmed.reference,
      scheduled_for: confirmed.scheduledFor || null
    });

    return {
      success: true,
      message: `Inspection booked with ${vendor.vendor_name}`,
      details: { vendor: vendor.vendor_name, booking_ref: confirmed.reference }
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    await logTrigger(dealId, 'INSPECTION_BOOKING_INITIATED', false, msg);
    return { success: false, message: msg };
  }
};

/**
 * TRIGGER: paid → charge the lot load fee.
 *
 * Winnie, 13 Sep: "$250... auto-applied the moment a vehicle reaches Paid - Ready for Sale,
 * same trigger point as the other inflow costs, so nobody has to remember to add it per
 * vehicle." This is that trigger point, and it is the same one the PPSR discharge and the
 * invoice already fire on, so the fee cannot drift onto a different moment in the lifecycle.
 *
 * The write itself is idempotent (see lotLoadFeeService), which matters here because a deal
 * can arrive at `paid` more than once.
 */
export const triggerLotLoadFee = async (dealId: string): Promise<WorkflowResult> => {
  try {
    const r = await applyLotLoadFee(dealId);
    await logTrigger(dealId, 'LOT_LOAD_FEE_APPLIED', true, r.message, { amount: r.amount, applied: r.applied });
    return { success: true, message: r.message };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    await logTrigger(dealId, 'LOT_LOAD_FEE_APPLIED', false, msg);
    return { success: false, message: msg };
  }
};

/**
 * TRIGGER 4: paid → Generate PPSR discharge certificate
 * Framework: Compulsory when deal reaches paid status
 */
export const triggerPPSR_Discharge = async (dealId: string): Promise<WorkflowResult> => {
  try {
    const { data: deal, error } = await supabase
      .from('deals')
      .select('id, autograb_vin, vehicle_vin, seller_name, autograb_year, autograb_make, autograb_model, vehicle_year, vehicle_make, vehicle_model')
      .eq('id', dealId)
      .single();

    if (error) throw error;

    const vin = deal.autograb_vin || deal.vehicle_vin;
    const year = deal.autograb_year ?? deal.vehicle_year;
    const make = deal.autograb_make || deal.vehicle_make || '';
    const model = deal.autograb_model || deal.vehicle_model || '';

    // Call PPSR service (real PPSR Cloud check via /api/ppsr/revalidate)
    const result = await ppsrService.generateDischarge(
      dealId,
      vin,
      deal.seller_name,
      `${year} ${make} ${model}`.trim()
    );

    // Log to workflow_logs
    await logTrigger(dealId, 'PPSR_DISCHARGE_INITIATED', result.success, result.message, {
      vin: deal.autograb_vin,
      certificate_path: result.details?.certificate_path
    });

    return result;
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    await logTrigger(dealId, 'PPSR_DISCHARGE_INITIATED', false, msg);
    return { success: false, message: msg };
  }
};

/**
 * TRIGGER 5: paid → Generate tax invoice
 * Framework C.3: Invoice triggers AR Collections workflow
 */
export const triggerInvoiceGeneration = async (dealId: string): Promise<WorkflowResult> => {
  try {
    const { data: deal, error } = await supabase
      .from('deals')
        .select('id, sale_price, autograb_market_value, valuation_amount, autograb_year, autograb_make, autograb_model')
      .eq('id', dealId)
      .single();

    if (error) throw error;

    /**
     * The buyer is invoiced for the price the buyer agreed, GST included. This used to pass
     * the AutoGrab estimate or the buy-in figure - what the vehicle cost Direct Auto - which
     * is a different number belonging to a different side of the deal. The sale price wins;
     * the old fallbacks remain only so a deal with no sale price still raises something
     * rather than an invoice for nothing.
     */
    const salePrice = deal.sale_price ?? deal.autograb_market_value ?? deal.valuation_amount ?? 0

    // Call invoice service (mocked for now)
    const result = await invoiceService.generateInvoice(
      dealId,
      salePrice,
      0 // v2.0-NL.2.0: no holding deposit
    );

    // Log to workflow_logs
    await logTrigger(dealId, 'INVOICE_GENERATED', result.success, result.message, {
      purchase_price: salePrice,
      invoice_number: result.details?.invoice_number
    });

    // Email the invoice to the buyer, then mark it as sent (Framework Part J / AR).
    if (result.success && result.details?.invoice_id) {
      try {
        const { data: inv } = await supabase
          .from('invoices')
          .select('id, invoice_number, total_amount, due_date, buyer_name, buyer_email')
          .eq('id', result.details.invoice_id)
          .single();

        if (inv?.buyer_email) {
          const dueDate = new Date(inv.due_date).toLocaleDateString('en-AU');
          const emailRes = await emailService.sendInvoice(
            inv.buyer_email,
            inv.buyer_name || 'Buyer',
            inv.invoice_number,
            Number(inv.total_amount),
            dueDate,
            { dealId, trigger: 'INVOICE_EMAILED' }
          );
          await logTrigger(dealId, 'INVOICE_EMAILED', emailRes.success, emailRes.message, {
            email: inv.buyer_email,
            invoice_number: inv.invoice_number
          });
          if (emailRes.success) {
            await invoiceService.markAsSent(inv.id);
          }
        } else {
          await logTrigger(dealId, 'INVOICE_EMAILED', false, 'No buyer email on invoice', {
            invoice_id: result.details.invoice_id
          });
        }
      } catch (e) {
        const em = e instanceof Error ? e.message : 'Invoice email failed';
        await logTrigger(dealId, 'INVOICE_EMAILED', false, em);
      }
    }

    return result;
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    await logTrigger(dealId, 'INVOICE_GENERATED', false, msg);
    return { success: false, message: msg };
  }
};

/**
 * TRIGGER 6: paid → Sync to Xero
 * Framework: Accounting integration, non-blocking if fails
 */
export const triggerXeroSync = async (dealId: string): Promise<WorkflowResult> => {
  try {
    // Call Xero service (mocked for now)
    const result = await xeroService.syncDealToXero(dealId);

    // Log to workflow_logs
    await logTrigger(dealId, 'XERO_SYNC_INITIATED', result.success, result.message, {
      xero_reference: result.details?.xero_reference
    });

    return result;
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    await logTrigger(dealId, 'XERO_SYNC_INITIATED', false, msg);
    // Don't throw - Xero sync is non-critical
    return { success: false, message: msg };
  }
};

/**
 * Log trigger execution to workflow_logs for audit trail
 * Non-blocking: Always completes regardless of result
 */
const logTrigger = async (
  dealId: string,
  triggerName: string,
  outcome: boolean | WorkflowLogStatus,
  message: string,
  details?: Record<string, any>
): Promise<void> => {
  try {
    await supabase
      .from('workflow_logs')
      .insert([
        {
          deal_id: dealId,
          trigger_name: triggerName,
          ...workflowLogFields(
            outcome === true ? 'success' : outcome === false ? 'failed' : outcome,
            message,
          ),
          details: details || {},
          executed_at: new Date().toISOString()
        }
      ]);
  } catch (err) {
    console.error(`Failed to log workflow trigger ${triggerName}:`, err);
    // Don't throw - logging failure shouldn't break workflow
  }
};

/**
 * Retry failed trigger
 * Used by admin dashboard to manually retry failed triggers
 */
export const retryFailedTrigger = async (
  workflowLogId: string,
  userId: string
): Promise<WorkflowResult> => {
  try {
    // Fetch the failed log entry
    const { data: log, error } = await supabase
      .from('workflow_logs')
      .select('deal_id, trigger_name, trigger_status, success')
      .eq('id', workflowLogId)
      .single();

    /* A legacy spelling of a real failure is still a failure, and a skip is never
       retryable. Both answers come from the one place. */
    if (error || !log || workflowLogStatus(log) !== 'failed') {
      return { success: false, message: 'Log entry not found or already succeeded' };
    }

    // Re-execute the trigger based on name
    let result: WorkflowResult = { success: false, message: 'Unknown trigger' };

    switch (log.trigger_name) {
      case 'BOS_SMS_SENT':
        result = await triggerBOS_SMS(log.deal_id);
        break;
      case 'BOS_EMAIL_SENT':
        result = await triggerBOS_Email(log.deal_id);
        break;
      case 'DD_COLLECTION_INITIATED':
        result = await triggerDD_Collection(log.deal_id);
        break;
      case 'INSPECTION_BOOKING_INITIATED':
        result = await triggerInspectionBooking(log.deal_id);
        break;
      case 'PPSR_DISCHARGE_INITIATED':
        result = await triggerPPSR_Discharge(log.deal_id);
        break;
      case 'INVOICE_GENERATED':
        result = await triggerInvoiceGeneration(log.deal_id);
        break;
      case 'XERO_SYNC_INITIATED':
        result = await triggerXeroSync(log.deal_id);
        break;
    }

    // Update log with retry info
    if (result.success) {
      await supabase
        .from('workflow_logs')
        .update({
          retried_at: new Date().toISOString(),
          retried_by_user_id: userId,
          trigger_status: 'success',
          success: true
        })
        .eq('id', workflowLogId);
    }

    return result;
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, message: msg };
  }
};
